import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '../../components/ui/Button';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Card } from '../../components/ui/Card';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { Item, ItemType, UnitType } from '../../types/item.types';
import { AppTheme } from '../../AppTheme';
import { getItemById, updateItem } from '../../service/item/ItemServices';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';

// Item update DTO
interface UpdateItemDto {
  item_code?: string;
  item_name: string;
  description?: string;
  unit_price: number;
  unit_type?: string;
}

const gstRateOptions = [
  { label: "0%", value: 0 },
  { label: "5%", value: 5 },
  { label: "10%", value: 10 },
  { label: "12%", value: 12 },
  { label: "18%", value: 18 },
  { label: "20%", value: 20 },
  { label: "28%", value: 28 },
  { label: "30%", value: 30 },
];


// Item type options
const itemTypeOptions = [
  { label: 'Product', value: 'product' },
  { label: 'Service', value: 'service' },
  { label: 'Hourly Rate', value: 'hourly' },
  { label: 'Project Phase', value: 'project_phase' }
];

// Unit type options
const unitTypeOptions = [
  { label: 'Each', value: 'each' },
  { label: 'Hour', value: 'hour' },
  { label: 'Day', value: 'day' },
  { label: 'Project', value: 'project' }
];

// Tax rate options (simplified for demo)
const taxRateOptions = [
  { label: 'No Tax (0%)', value: 1 },
  { label: 'Sales Tax (10%)', value: 2 },
  { label: 'VAT (20%)', value: 3 },
  { label: 'GST (5%)', value: 4 }
];

export const EditItem: React.FC = () => {
  const localization = useSelector((state: any) => state.localization);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = useRef<Toast>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [item, setItem] = useState<Item | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const fetchItem = async () => {
      if (!id) return;

      try {
        setIsLoading(true);
        const response = await getItemById(dispatch, id);

        if (response) {
          setItem(response);
        } else {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: response?.message || 'Failed to load item data',
            life: 3000
          });
        }
      } catch (error) {
        console.error('Error fetching item:', error);
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load item data. Please try again.',
          life: 3000
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchItem();
  }, [id, dispatch]);

  const handleChange = (field: keyof Item, value: any) => {
    if (!item) return;

    setErrors(prev => ({ ...prev, [field]: '' }));

    setItem(prev => {
      if (!prev) return prev;

      return {
        ...prev,
        [field]: value
      };
    });
  };

  const validateForm = (): boolean => {
    if (!item) return false;

    const newErrors: Record<string, string> = {};

    if (!item.item_code?.trim()) {
      newErrors.item_name = 'Item code is required';
    }

    if (!item.item_name?.trim()) {
      newErrors.item_name = 'Item name is required';
    }

    if (item.unit_price === undefined || parseInt(item.unit_price) <= 0) {
      newErrors.unit_price = 'Unit price must be greater than zero';
    }

    if (!item.unit_type) {
      newErrors.unit_type = 'Unit type is required';
    }
    if (item.sac_gst_rate===null || item.sac_gst_rate===undefined) {
      newErrors.sac_gst_rate = 'Service tax is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const prepareRequestData = (): Partial<Item> => {
    if (!item) {
      throw new Error('Item data is missing');
    }

    return {
      item_code: item.item_code,
      item_name: item.item_name,
      description: item.description,
      unit_price: item.unit_price,
      unit_type: item.unit_type,
      sac_gst_rate: item.sac_gst_rate
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.current?.show({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please correct the errors in the form',
        life: 3000
      });
      return;
    }

    if (!id) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Item ID is missing',
        life: 3000
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const itemData = prepareRequestData();
      const response = await updateItem(dispatch, parseInt(id), itemData);

      if (response) {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Item updated successfully',
          life: 3000
        });

        // After successful update, redirect to item list after a short delay
        setTimeout(() => {
          navigate('/item');
        }, 1500);
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: response?.message || 'Failed to update item',
          life: 3000
        });
      }
    } catch (error) {
      console.error('Error updating item:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update item. Please try again.',
        life: 3000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Conditional rendering for appropriate unit type options based on item type
  const getUnitTypeOptions = () => {
    if (!item) return unitTypeOptions;

    switch (item.item_type) {
      case 'product':
        return unitTypeOptions.filter(option => option.value === 'each');
      case 'service':
        return unitTypeOptions.filter(option => ['hour', 'day', 'project'].includes(option.value));
      case 'hourly':
        return unitTypeOptions.filter(option => option.value === 'hour');
      case 'project_phase':
        return unitTypeOptions.filter(option => option.value === 'project');
      default:
        return unitTypeOptions;
    }
  };

  // Update unit type when item type changes
  const handleItemTypeChange = (value: ItemType) => {
    if (!item) return;

    let newUnitType: UnitType;

    // Set appropriate default unit type based on item type
    switch (value) {
      case 'product':
        newUnitType = 'each';
        break;
      case 'service':
        newUnitType = 'hour';
        break;
      case 'hourly':
        newUnitType = 'hour';
        break;
      case 'project_phase':
        newUnitType = 'project';
        break;
      default:
        newUnitType = 'each';
    }

    setItem(prev => {
      if (!prev) return prev;

      return {
        ...prev,
        item_type: value,
        unit_type: newUnitType
      };
    });

    setErrors(prev => ({
      ...prev,
      item_type: '',
      unit_type: ''
    }));
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-6">
        <ProgressSpinner style={{ width: '50px', height: '50px' }} />
        <span className="ml-3">Loading item data...</span>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="p-4">
        <h2 className="text-lg font-medium text-gray-700">Item not found</h2>
        <p className="text-gray-600 mt-2">The requested item could not be found or you don't have permission to view it.</p>
        <Button
          className="mt-4"
          onClick={() => navigate('/item')}
        >
          <FontAwesomeIcon icon={faArrowLeft} />{"  "}
          Back to Items
        </Button>
      </div>
    );
  }

  const handleInvalidValue = () => {
    toast.current?.show({
      severity: "warn",
      summary: "Invalid GST Rate",
      detail: "GST rate cannot be negative. Reset to 0%.",
      life: 3000,
    });
    handleChange('sac_gst_rate', 0)
    handleChange('sacCode', null)
  };

  return (
    <div>
      <Toast ref={toast} />

      <div className="flex justify-between items-center mb-6">
        <h1 className={`text-${AppTheme.font.size['2xl']} font-bold text-gray-800`}>Edit Item - {item.item_name}</h1>

        <Button
          className="p-button-outlined"
          onClick={() => navigate('/item')}
        >
          <FontAwesomeIcon icon={faXmark} />{"  "}
          Cancel
        </Button>
      </div>

      <form onSubmit={handleSubmit}>
        <Card className="mb-6">
          <div className="p-fluid grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="col-span-2">
              <h2 className="text-xl font-semibold mb-4">Item Information</h2>
            </div>

            <div className="field">
              <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">Item Code</label>
              <InputText
                id="code"
                value={item.item_code}
                onChange={(e) => handleChange('item_code', e.target.value)}
                required
              />
            </div>

            <div className="field">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <InputText
                id="name"
                value={item.item_name}
                onChange={(e) => handleChange('item_name', e.target.value)}
                className={errors.item_name ? 'p-invalid' : ''}
                required
              />
              {errors.item_name && (
                <small className="p-error">{errors.item_name}</small>
              )}
            </div>

            <div className="field">
              <label htmlFor="unitPrice" className="block text-sm font-medium text-gray-700 mb-1">Unit Price *</label>
              <InputNumber
                id="unitPrice"
                value={typeof item.unit_price === 'string' ? parseFloat(item.unit_price) : item.unit_price}
                onValueChange={(e) => handleChange('unit_price', e.value)}
                mode="currency"
                currency="USD"
                locale="en-US"
                minFractionDigits={2}
                className={errors.unit_price ? 'p-invalid' : ''}
                required
              />
              {errors.unit_price && (
                <small className="p-error">{errors.unit_price}</small>
              )}
            </div>

            <div className="field">
              <label htmlFor="unitType" className="block text-sm font-medium text-gray-700 mb-1">Unit Type *</label>
              <Dropdown
                id="unitType"
                value={item.unit_type}
                options={getUnitTypeOptions()}
                onChange={(e) => handleChange('unit_type', e.value)}
                placeholder="Select Unit Type"
                className={errors.unit_type ? 'p-invalid' : ''}
                required
              />
              {errors.unit_type && (
                <small className="p-error">{errors.unit_type}</small>
              )}
            </div>
            <div className="field">
              <label htmlFor="unit_type" className="block text-sm font-medium text-gray-700 mb-1">Service Tax *</label>
              <Dropdown
                value={`${(item.sac_gst_rate)}%`}
                options={gstRateOptions}
                onChange={(e) => {
                  const rawValue = e.target?.value ?? e.value;

                  const stringValue = String(rawValue); // Ensure it's a string

                  const cleanInput = stringValue.includes('%')
                    ? stringValue.replace('%', '')?.trim()
                    : stringValue?.trim();

                  handleChange('sac_gst_rate', cleanInput);
                  // setErrors({ ...errors, sac_gst_rate: false });
                }}
                placeholder={localization?.SelectGSTRate || "Select GST Rate"}
                className="w-full"
                editable={true}
                onBlur={(e) => {
                  // Safely try to get the input
                  const rawInput =
                    e?.target?.value ??
                    e?.target?.innerText ??
                    item.sac_gst_rate?.toString() ??
                    "";

                  const cleaned = rawInput.replace("%", "").trim();

                  if (cleaned === "") {
                    handleChange('sac_gst_rate', 0)
                    handleChange('sacCode', null)
                    return;
                  }

                  const numeric = parseFloat(cleaned);

                  if (isNaN(numeric)) {
                    handleChange('sac_gst_rate', 0)
                    handleChange('sacCode', null)
                    return;
                  }

                  if (numeric < 0) {
                    handleInvalidValue();
                  } else {
                    handleChange('sac_gst_rate', numeric)
                    handleChange('sacCode', null)
                  }
                }}
              />

              {errors.sac_gst_rate && (
                <small className="p-error">Service tax is required</small>
              )}
            </div>

            <div className="field col-span-2">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <InputTextarea
                id="description"
                value={item.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={5}
              />
            </div>
          </div>
        </Card>

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            className="p-button-outlined"
            onClick={() => navigate('/item')}
          >
            <FontAwesomeIcon icon={faXmark} />{"  "}
            Cancel
          </Button>
          <Button
            type="submit"
            icon="pi pi-check"
            isLoading={isSubmitting}
          >
            <FontAwesomeIcon icon={faCheck} />{"  "}
            Save Changes
          </Button>
        </div>
      </form>
    </div>
  );
};